import React, { useEffect, useState } from 'react'
import { Alert, Button, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import OperationComparisonView from './OperationComparisonView'
import OperationUpdateImpactDialog from './OperationUpdateImpactDialog'
import PublishOperationDialog from './PublishOperationDialog'
import { getOperationDetail, readOperationLibraryError } from './operationLibraryApi'
import { getOperationCustomization, readProcessError } from './processApi'
import { OPERATION_LINK_STATE } from './operationLinkIntegrity'

/**
 * OperationCustomizationDialog (E4, §8.8) — las TRES acciones de CA1.9 sobre un nodo con vínculo
 * vivo, en una sola superficie:
 *
 * 1. **Ver qué difiere** (J24, S38 · `RN-01.22`): pide P2
 *    (`GET workflow/{id}/node/{nodeId}/operationCustomization`, `processApi.js`) y pinta su
 *    `comparison` —el `OperationComparison` de #130 VERBATIM— con el mismo `OperationComparisonView`
 *    que usan el impacto (N14) y la previsualización de publicación (N16): una comparación se lee
 *    igual venga de donde venga. Bajo el título "Compare versions" (J30). Es **solo lectura**: no
 *    toca el nodo, ni la versión, ni el editor.
 * 2. **Adoptar una versión** (J25, S40 · `RN-01.23`): se elige una versión de la MISMA operación
 *    —incluida la ya vinculada, que es como se descarta la personalización (`RN-01.23b`)— y se
 *    confirma en el diálogo de impacto de #130 (C6), que es quien añade el aviso de pérdida (J26)
 *    cuando N14 dice `customizationsWillBeLost`. No hay endpoint de adoptar: lo que queda es la
 *    intención `UPDATE` del guardado general.
 * 3. **Proponer a la librería** (J27, S44 · `RN-01.24`): el mismo `PublishOperationDialog` de #130
 *    (C4) con el código BLOQUEADO al de la operación vinculada. Tampoco hay endpoint: queda la
 *    intención `PUBLISH` con `asNewVersionOfCode`.
 *
 * Las dos acciones que cambian algo exigen el editor en modo EDICIÓN (`RN-G12`: todo lo de esta
 * historia es un cambio pendiente del editor); ver diferencias, no. Proponer exige además
 * `OPERATION_LIBRARY_EDIT` y un nodo `CUSTOMIZED`: sobre uno `SYNCHRONIZED` no hay nada que
 * proponer (`RN-01.24b`).
 */
const OperationCustomizationDialog = ({
  open = false,
  workflowId = 0,
  nodeId = '',
  operationLink = {},
  editing = false,
  canPropose = false,
  featureGroupRevisionControlled = false,
  jobReference = {},
  featureGroupBaselines = {},
  onAdopt = () => {},
  onPropose = () => {},
  onClose = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [customization, setCustomization] = useState(null)
  const [error, setError] = useState('')
  const [versions, setVersions] = useState([])
  const [choosingVersion, setChoosingVersion] = useState(false)
  const [adoptTarget, setAdoptTarget] = useState('')
  const [proposing, setProposing] = useState(false)

  useEffect(() => {
    let current = true
    if (!open || !workflowId || !nodeId) {
      setCustomization(null)
      setVersions([])
      setChoosingVersion(false)
      setAdoptTarget('')
      setProposing(false)
      return () => { current = false }
    }
    setError('')
    getOperationCustomization(workflowId, nodeId)
      .then((result) => { if (current) setCustomization(result || null) })
      .catch((thrown) => { if (current) setError(readProcessError(thrown).error) })
    return () => { current = false }
  }, [open, workflowId, nodeId])

  // La autoridad del estado y del código es el servidor (`RN-01.16`); el vínculo que el panel ya
  // tiene solo sirve mientras P2 no ha contestado.
  const code = customization?.code || operationLink?.code || ''
  const linkedVersionNumber = Number(customization?.versionNumber || operationLink?.versionNumber || 0)
  // Cuenta como personalizado lo que diga CUALQUIERA de las dos fuentes: el servidor (P2, que
  // compara los jobs PERSISTIDOS) o el estado que el panel pinta ahora mismo, que adelanta la
  // personalización hecha en pantalla y todavía sin guardar (`RN-01.15`). Lo que se propone a la
  // librería es lo que hay en el editor, no lo que había en la última escritura.
  const isCustomized = customization?.state === OPERATION_LINK_STATE.CUSTOMIZED ||
    operationLink?.state === OPERATION_LINK_STATE.CUSTOMIZED

  const chooseVersion = async () => {
    if (!code) return
    setError('')
    try {
      const detail = await getOperationDetail(code)
      const loaded = Array.isArray(detail?.versions) ? detail.versions : []
      // `E-LNK-04` sigue rechazando una versión ANTERIOR a la vinculada: ofrecerla sería invitar al
      // usuario a un error del servidor. La vinculada sí se ofrece (`RN-01.23b`).
      setVersions(loaded
        .filter((version) => Number(version.versionNumber || 0) >= linkedVersionNumber)
        .sort((one, other) => Number(other.versionNumber || 0) - Number(one.versionNumber || 0)))
      setChoosingVersion(true)
    } catch (thrown) {
      setError(readOperationLibraryError(thrown).error)
    }
  }

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_('Compare versions')}</Typography> }}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      renderComponent={
        <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }} data-testid='operation-customization-dialog'>
          {error ? <Alert severity='error' data-testid='operation-customization-error'>{t_(error)}</Alert> : null}

          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {editing
              ? (
                <Button size='small' variant='outlined' data-testid='operation-adopt-version' onClick={chooseVersion}>
                  {t_('Adopt version')}
                </Button>
                )
              : null}
            {editing && canPropose && isCustomized
              ? (
                <Button size='small' variant='outlined' data-testid='operation-propose-to-library' onClick={() => setProposing(true)}>
                  {t_('Propose to library')}
                </Button>
                )
              : null}
          </Stack>

          {choosingVersion
            ? (
              <List dense data-testid='operation-adopt-version-list'>
                {versions.map((version) => (
                  <ListItemButton
                    key={version.nodeId}
                    data-testid={'operation-adopt-version-' + version.versionNumber}
                    onClick={() => setAdoptTarget(version.nodeId)}
                  >
                    <ListItemText
                      primary={t_('Version') + ' ' + version.versionNumber}
                      secondary={version.indexValue?.name || ''}
                    />
                  </ListItemButton>
                ))}
              </List>
              )
            : null}

          <OperationComparisonView comparison={customization?.comparison} />

          {/* El impacto se monta solo mientras está abierto, como en la cabecera del nodo (C7): así
              cada apertura parte de cero y vuelve a preguntar a N14. */}
          {adoptTarget
            ? (
              <OperationUpdateImpactDialog
                open
                workflowId={workflowId}
                nodeId={nodeId}
                targetNodeId={adoptTarget}
                featureGroupRevisionControlled={featureGroupRevisionControlled}
                jobReference={jobReference}
                featureGroupBaselines={featureGroupBaselines}
                onConfirm={(targetNodeId) => {
                  setAdoptTarget('')
                  setChoosingVersion(false)
                  onAdopt(targetNodeId)
                }}
                onClose={() => setAdoptTarget('')}
              />
              )
            : null}

          {proposing
            ? (
              <PublishOperationDialog
                open
                workflowId={workflowId}
                nodeId={nodeId}
                lockedCode={code}
                onPublish={(publish) => {
                  setProposing(false)
                  onPropose(publish)
                }}
                onClose={() => setProposing(false)}
              />
              )
            : null}
        </Stack>
      }
    />
  )
}

export default OperationCustomizationDialog
